import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext, useEffect } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/home/ModalContext';
import buildHomeSearchRedirectUrl from '~/utilities/composer/buildHomeSearchRedirectUrl';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const isComposerReady = useIsComposerReady();
  const isNative = useIsNative();
  const { zip } = useUserLocation();

  // Listeners
  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ filters, source }) => {
        if (source !== 'composer' || isNative) return;

        const validatedFilters = parseComposerFilters(filters);
        if (!validatedFilters) return;

        window.location.href = buildHomeSearchRedirectUrl(validatedFilters);
      },
      [isNative]
    )
  );
  // Non-SPA nav (Home is Pages Router, VDP is App Router), so this unmounts
  // everything on navigation — no need to explicitly close composer first,
  // unlike the SPA case on SRP.
  useCoreEvents(
    eventNames.composer.mfe.vdp,
    useCallback(
      ({ source, vehicleId }) => {
        if (source !== 'composer' || isNative || !vehicleId) return;

        window.location.href = `/vehicle/${vehicleId}`;
      },
      [isNative]
    )
  );

  // Dispatchers
  useEffect(() => {
    if (!isComposerReady) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: ['update_search', 'vdp'],
        url: window.location.href,
        zip5: zip
      },
      source: 'page',
      surface: 'home'
    });
  }, [isComposerReady, zip]);
}
